import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface OpenQuestionItemState {
  text: string;
  textChanged: boolean;
  complete: boolean;
  resubmitted?: boolean;
}

export type OpenQuestionSliceState = Record<string, OpenQuestionItemState>;

const getDefaultState = (): OpenQuestionItemState => ({
  text: "",
  textChanged: false,
  complete: false,
});

const openQuestionSlice = createSlice({
  name: "oeq",
  initialState: {} as OpenQuestionSliceState,
  reducers: {
    update: (
      state,
      action: PayloadAction<{ componentID: string; text: string }>
    ) => {
      const { componentID, text } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].text = text;
      state[componentID].textChanged = true;
    },
    submit: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      if (state[componentID]) {
        if (state[componentID].complete && state[componentID].textChanged) {
          state[componentID].resubmitted = true;
        }
        state[componentID].complete = true;
        state[componentID].textChanged = false;
      }
    },
    reset: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      state[componentID] = getDefaultState();
    },
  },
  extraReducers: (builder) => {
    const isLegacy = (action: any, type: string) =>
      action.type === type && !action._fromTranslator;

    builder
      .addMatcher(
        (action) => isLegacy(action, "UPDATE_OEQ"),
        (state, action: any) => {
          const { componentID, text } = action.payload;
          if (!state[componentID]) {
            state[componentID] = getDefaultState();
          }
          state[componentID].text = text;
          state[componentID].textChanged = true;
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "SUBMIT_OEQ"),
        (state, action: any) => {
          const componentID = action.payload;
          if (state[componentID]) {
            if (state[componentID].complete && state[componentID].textChanged) {
              state[componentID].resubmitted = true;
            }
            state[componentID].complete = true;
            state[componentID].textChanged = false;
          }
        }
      )
      .addMatcher(
        (action) => isLegacy(action, "RESET_OEQ"),
        (state, action: any) => {
          const componentID = action.payload;
          state[componentID] = getDefaultState();
        }
      );
  },
});

export const openQuestionActions = openQuestionSlice.actions;
export const openQuestionReducer = openQuestionSlice.reducer;

export const selectOpenQuestionState = (
  state: { oeq: OpenQuestionSliceState },
  componentId: string
) => state.oeq?.[componentId] || getDefaultState();

/** @deprecated Use openQuestionActions */
export const oeqActions = openQuestionActions;
/** @deprecated Use openQuestionReducer */
export const oeqReducer = openQuestionReducer;
/** @deprecated Use selectOpenQuestionState */
export const selectOEQState = selectOpenQuestionState;
/** @deprecated Use OpenQuestionSliceState */
export type OEQState = OpenQuestionSliceState;
